import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { billingService } from '../services/billing.service';

interface UpgradeModalProps {
  isOpen: boolean;
  plan: 'PRO' | 'TEAM' | null;
  onClose: () => void;
  onSuccess: () => void;
}

export const UpgradeModal: React.FC<UpgradeModalProps> = ({ isOpen, plan, onClose, onSuccess }) => {
  const { token, user } = useAuth();
  const [isUpgrading, setIsUpgrading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [orgName, setOrgName] = useState('');

  if (!isOpen || !plan || !user) return null;

  const today = new Date();
  const nextMonth = new Date(today.getFullYear(), today.getMonth() + 1, today.getDate());
  
  const dateFormatter = new Intl.DateTimeFormat('en-CA', { month: 'long', day: 'numeric', year: 'numeric' });
  const formattedToday = dateFormatter.format(today);
  const formattedNextMonth = dateFormatter.format(nextMonth);

  const price = plan === 'PRO' ? 3.00 : 5.00;
  const planName = plan === 'PRO' ? 'Pro Plan' : 'Team Plan';

  const handleProceed = async (e: React.FormEvent) => {
    e.preventDefault();
    if (plan === 'TEAM' && !orgName.trim()) {
      setError('Organization name is required.');
      return;
    }

    setIsUpgrading(true);
    setError(null);

    try {
      const { checkoutToken } = await billingService.initHelcimCheckout(token!, plan, 1, orgName);
      
      const handleHelcimResponse = async (event: MessageEvent) => {
        const data = event.data;
        
        // Ensure the message is actually from Helcim and has the correct shape
        if (!data || typeof data.eventName !== 'string') return;

        // The modern HelcimPay.js appends the checkoutToken to the eventName
        if (data.eventName === `helcim-pay-js-${checkoutToken}`) {
          
          // We received a valid response, remove the listener so we don't process duplicate events
          window.removeEventListener('message', handleHelcimResponse);

          if (data.eventStatus === 'SUCCESS') {
            // Helcim docs state eventMessage is an object on SUCCESS
            const txId = data.eventMessage.transactionId;
            try {
              await billingService.verifyHelcimTransaction(token!, txId, plan, 1, orgName);
              onSuccess();
              onClose();
            } catch (err: any) {
              setError(err.message || 'Payment verification failed.');
              setIsUpgrading(false);
            }
          } 
          else if (data.eventStatus === 'ABORTED') {
            // Helcim docs state this triggers on a declined payment
            setError('Payment was declined. Please check your card details and try again.');
            setIsUpgrading(false);
          } 
          else if (data.eventStatus === 'HIDE') {
            // Helcim docs state this triggers when the user closes the modal before paying
            setIsUpgrading(false);
          }
        }
      };

      // Attach the listener before invoking the iframe
      window.addEventListener('message', handleHelcimResponse);
      
      // @ts-ignore
      if (window.appendHelcimPayIframe) {
          // @ts-ignore
          window.appendHelcimPayIframe(checkoutToken);
      } else {
          throw new Error('Payment gateway failed to load. Please refresh the page.');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to initialize checkout.');
      setIsUpgrading(false);
    }
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-md overflow-hidden flex flex-col">
        <div className="flex justify-between items-center p-5 border-b border-gray-200">
          <h2 className="text-xl font-bold text-gray-800">Upgrade to {planName}</h2>
          {/* Close button is disabled while the Helcim modal is actively open */}
          <button onClick={onClose} disabled={isUpgrading} className="text-gray-400 hover:text-gray-600 text-xl font-bold disabled:opacity-50">✕</button>
        </div>

        <form onSubmit={handleProceed} className="flex-1 p-6 bg-gray-50 flex flex-col gap-5">
          {error && <div className="p-3 bg-red-50 text-red-600 border border-red-200 rounded text-sm">{error}</div>}

          {plan === 'TEAM' && (
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-1">Organization Name</label>
              <input 
                type="text" 
                required 
                placeholder="e.g. Acme Corp" 
                value={orgName}
                onChange={(e) => setOrgName(e.target.value)}
                disabled={isUpgrading}
                className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 outline-none disabled:bg-gray-100 disabled:text-gray-400"
              />
            </div>
          )}

          <div className="bg-white border border-gray-200 rounded shadow-sm p-4 space-y-3">
            <h3 className="text-sm font-bold text-gray-500 uppercase tracking-wider border-b border-gray-100 pb-2">Subscription Details</h3>
            
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Plan:</span>
              <span className="font-bold text-gray-900">{planName}</span>
            </div>
            
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Due Today ({formattedToday}):</span>
              <span className="font-bold text-green-700">${price.toFixed(2)} CAD</span>
            </div>

            <div className="flex justify-between text-sm pt-2 border-t border-gray-100">
              <span className="text-gray-600">Next Billing Date:</span>
              <span className="font-medium text-gray-900">{formattedNextMonth}</span>
            </div>

            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Recurring Amount:</span>
              <span className="font-medium text-gray-900">${price.toFixed(2)} CAD / month</span>
            </div>
          </div>

          <p className="text-xs text-gray-500 text-center">
            By proceeding, you agree to be charged immediately for the first month. Your subscription will automatically renew every 30 days until canceled.
          </p>

          <button 
            type="submit" 
            disabled={isUpgrading}
            className="w-full py-3 bg-blue-600 text-white font-bold rounded shadow hover:bg-blue-700 transition-colors disabled:bg-blue-400"
          >
            {isUpgrading ? 'Loading Secure Checkout...' : 'Proceed to Payment'}
          </button>
        </form>
      </div>
    </div>
  );
};